
import Paper from '@mui/material/Paper'
import Clipboard from 'clipboard'
import { STATUS_TYPE, StatusIdea } from 'interfaces'
import { useMemo, useRef } from 'react'
import { useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router'
import { from as observableFrom, of as observableOf } from 'rxjs'
import { catchError } from 'rxjs/operators'
import { addFavoriteStatusIdea, removeFavoriteStatusIdea } from 'services/content/favorites'
import { favStateSelectorForItem } from 'services/content/selectors'
import { checkFeatureAvailability, getProductUpgradeByFeature, promptUpgrade } from 'services/product'
import { message } from 'services/snackbar'
import { openComposerContentDialog } from 'services/ui'
import { FEATURE_FIND_STATUS_IDEAS } from 'shared/constants'
import StoreState, { StoreThunkDispatch } from 'store/state'
import { fontSizesFor } from 'utils/fontSize'
import CardComposeButton from './CardComposeButton'
import ContentCardUpgradeOverlay from './ContentCardUpgradeOverlay'
import FavButton from './FavButton'
import styles from './StatusIdeaCard.pcss'

const MAX_STATUS_LENGTH_CENTERED = 300
export interface StatusIdeaCardProps {
  content: StatusIdea
  small?: boolean
  className?: string
  actionLabel?: React.ReactNode
  actionButtonIcon?: React.ReactNode
  onCompose?(content: StatusIdea): void | boolean
}

export function StatusIdeaCard(props: StatusIdeaCardProps) {
  const { content, small, actionLabel, className, actionButtonIcon, onCompose } = props
  const intl = useIntl()
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch<StoreThunkDispatch>()

  const clipboard = useRef<Clipboard | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const isHovered = useRef(false)
  const containerClassName = `${styles.card} ${styles.square} ${className || ''} ${small ? styles.small : ''}`
  const currentFavState = useSelector((state: StoreState) => favStateSelectorForItem(state, state, state, content.id, content.type))
  const isFavorite = currentFavState.added || (Boolean(content.isFavorite) && !currentFavState.removed)

  let favCount = content.favCount
  if (currentFavState.added) {
    favCount += 1
  } else if (currentFavState.removed && favCount > 0) {
    favCount -= 1
  }

  const statusesAllowed = useMemo(() => {
    return dispatch(checkFeatureAvailability(FEATURE_FIND_STATUS_IDEAS, true))
  }, [dispatch])

  const setRef = (element: HTMLElement | null) => {
    if (element) {
      if (clipboard.current) {
        (clipboard.current as any).destroy()
      }

      clipboard.current = new Clipboard(
        element,
        {
          text: () => {
            if (content.author) {
              return `${content.status}\n~${content.author}`
            }
            return content.status
          },
          container: element
        } as any
      )
      ; (clipboard.current as any).on('success', (e: any) => {
        e.clearSelection()
      })
      ; (clipboard.current as any).on('error', (e: any) => {
        console.log('Clipboard error', e)
      })
    }
  }

  const handleCompose = () => {
    if (onCompose) {
      const shouldAbort = onCompose(content)
      if (shouldAbort) {
        return
      }
    }
    if (!statusesAllowed) {
      return
    }
    dispatch(message(intl.formatMessage({ id: 'notifications.status-copied' })))
    navigate('/composer', { state: { floating: true, opener: location.pathname, background: location } })
  }

  const sendToAIWriter = () => {
    dispatch(openComposerContentDialog({ tab: 'generate', prompt: `${content.status}${content.author ? `\n~${content.author}` : ''}` }))
  }

  const onTransitionEnd = (e: React.TransitionEvent<HTMLDivElement>) => {
    const element = containerRef.current

    // EXPL: `transitionend` bubbles - only the card's own transition, which is transform and
    // nothing else, is the raise.
    if (!element || e.target !== element) {
      return
    }

    // EXPL: The grow and the shrink end on the same property, and the previous scale check read
    // both as collapsed (`--card-hover-scale` is 1.02, which `toFixed(1)` rounds to '1.0'), so a
    // steadily hovered card lost its raise as soon as it finished growing. The pointer flag says
    // which of the two just ended.
    if (isHovered.current) {
      return
    }

    element.classList.remove(styles.raised)
  }

  const onMouseEnter = () => {
    isHovered.current = true
    containerRef.current?.classList.add(styles.raised)
  }

  const onMouseLeave = () => {
    isHovered.current = false
  }

  const handleAddFavorite = () => {
    return observableFrom(dispatch(addFavoriteStatusIdea(content)).unwrap())
      .pipe(catchError(() => observableOf({ error: true })))
  }

  const handleRemoveFavorite = () => {
    return observableFrom(dispatch(removeFavoriteStatusIdea(content)).unwrap())
      .pipe(catchError(() => observableOf({ error: true })))
  }

  const onUpgrade = (feature: string) => {
    const product = dispatch(getProductUpgradeByFeature(feature))
    if (product) {
      dispatch(promptUpgrade({ feature, product: product.handles.annual }))
    }
  }

  const fontSizes = fontSizesFor(content.status)
  const style = fontSizes.normal
  const wrapperClassName = `${styles['content-box']} ${content.author ? styles['align-left'] : ''}`
  const contentClassName = `${styles.content} ${content.status.length <= MAX_STATUS_LENGTH_CENTERED ? styles['centered-y'] : ''}`

  return (
    <Paper
      className={containerClassName}
      ref={containerRef}
      onTransitionEnd={onTransitionEnd}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className={styles['content-wrapper']} data-testid="status-idea-card">
        <div key="content-box" className={wrapperClassName}>
          <div key="content" className={contentClassName} style={style}>
            {content.highlighted ? (
              <span key={content.id} dangerouslySetInnerHTML={{ __html: content.highlighted }} />
            ) : content.status}
            {content.author ? (
              <div className={styles.author} key="author">
                {`~${content.author}`}
              </div>
            ) : null}
          </div>
        </div>
        <div
          key="actions"
          className={styles['actions-row']}
          data-testid="status-idea-card-actions"
          onClick={(e) => e.stopPropagation()}
        >
          <FavButton
            key="favorite"
            isFavorite={isFavorite}
            count={favCount}
            size="small"
            className={styles['fav-btn']}
            hover={false}
            tooltipPlacement="top"
            onFavorite={handleAddFavorite}
            onUnfavorite={handleRemoveFavorite}
          />
        </div>
        <CardComposeButton
          ref={setRef}
          className={styles['compose-btn']}
          label={actionLabel}
          icon={actionButtonIcon}
          onClick={handleCompose}
        />
        {!statusesAllowed && (
          <ContentCardUpgradeOverlay
            key="upgrade"
            contentType={STATUS_TYPE}
            feature={FEATURE_FIND_STATUS_IDEAS}
            favButton={null}
            className={styles['upgrade-overlay']}
            onUpgrade={onUpgrade}
          />
        )}
      </div>
    </Paper>
  )
}

export default StatusIdeaCard
